import _ from 'underscore';
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Panel, PanelType, Overlay } from '@bingads-webui-react/fabric-control';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { findParentElementWithClass } from '@bingads-webui-campaign-react/asset-library-model';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  PANEL_WIDTH,
  layerProps,
  panelStylesProp,
  panelStyles,
} from './styles/panel-container-style';

const overlayStyles = {
  root: { zIndex: 2 },
};

const getStyle = () => ({
  addMediaPanel: {
    ...panelStyles,
  },
  panelHeader: {
    paddingLeft: 24,
    paddingRight: 24,
    alignSelf: 'flex-start',
    flexGrow: 1,
  },
  panelTitle: {
    fontSize: 20,
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: '27px',
  },
});

export const AssetLibraryAddMediaPanelContainer = (props) => {
  const {
    onRenderFooterContent,
    isOpen,
    dataDirty,
    dismiss,
    onDismissedBegin,
    onRenderHeaderContent,
    showBlocker,
    headerText,
    customWidth,
    styles,
    navigation,
    className,
    children,
  } = props;
  //
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyle);

  // don't allow to close panel by losing focus
  const onDismiss = useCallback((evt) => {
    const target = evt && evt.currentTarget;
    const addMediaPanelElem = findParentElementWithClass(target, classes.addMediaPanel);
    if (addMediaPanelElem) {
      dismiss();
    }
  }, [classes.addMediaPanel, dismiss]);

  const onDismissed = useCallback(() => {
    onDismissedBegin();
    navigation.goBack({ reload: dataDirty });
  }, [dataDirty, navigation, onDismissedBegin]);

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.custom}
      customWidth={customWidth}
      onDismiss={onDismiss}
      onDismissed={onDismissed}
      headerText={headerText}
      onRenderHeader={onRenderHeaderContent ? () => (
        <div className={classes.panelHeader}>
          <div className={classnames(classes.panelTitle)}>
            {headerText}
          </div>
          {onRenderHeaderContent()}
        </div>
      ) : undefined}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      styles={styles}
      layerProps={layerProps}
      className={classnames(classes.addMediaPanel, className)}
      // disable focus trap to avoid help popup not being able to receive focus
      focusTrapZoneProps={{ disabled: true }}
    >
      {
        showBlocker &&
        <Overlay isDarkThemed={false} styles={overlayStyles}>
          <AssetLibrarySpinner useCustomSize />
        </Overlay>
      }
      {children}
    </Panel>
  );
};

AssetLibraryAddMediaPanelContainer.propTypes = {
  onRenderFooterContent: PropTypes.func,
  onRenderHeaderContent: PropTypes.func,
  isOpen: PropTypes.bool,
  dataDirty: PropTypes.bool,
  dismiss: PropTypes.func,
  onDismissedBegin: PropTypes.func,
  showBlocker: PropTypes.bool,
  headerText: PropTypes.string,
  children: PropTypes.element,
  navigation: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  customWidth: PropTypes.number,
  styles: PropTypes.shape({}),
};

AssetLibraryAddMediaPanelContainer.defaultProps = {
  onRenderFooterContent: _.noop,
  isOpen: false,
  dataDirty: false,
  dismiss: _.noop,
  onDismissedBegin: _.noop,
  onRenderHeaderContent: null,
  showBlocker: false,
  headerText: '',
  children: null,
  className: '',
  customWidth: PANEL_WIDTH,
  styles: panelStylesProp,
};
